/**
 * PlanManager Component - REUTILIZÁVEL
 *
 * Fonte ÚNICA da tabela de planos (nome, valor, duração, cor). Usado por
 * Plans.jsx (página do bot), DisparoForm e UpsellForm — não duplique este markup.
 *
 * Responsabilidades:
 * - Gerenciar estado COMPLETO de planos (nomes, valores, durações, cores)
 * - CRUD: adicionar, editar, remover planos
 * - Renderizar a lista (cards expansíveis) e devolver a lista via callback
 *
 * Props:
 * - initialPlans?: { names: [], values: [], durations: [], color: [] }  (opcional)
 * - onUpdate: (plans) => void  // chamado quando os planos mudam
 * - label?: string   (opcional; "" esconde o rótulo)
 * - currencyMode?: 'brl' | 'usd' | 'mxn'  // controla o rótulo do valor (R$ / $ / MX$)
 * - baseCurrency?: 'MXN' | 'USD' | 'COP' | 'CLP'  // LATAM: moeda em que os valores são escritos
 *
 * SEM dependências do pai além das props — é autônomo.
 */
import { useState, useEffect } from 'react';
import { Package, ChevronDown, Trash2, Plus } from 'lucide-react';
import DeliveryTypeField from '../UI/DeliveryTypeSelect/DeliveryTypeField';
import { DELIVERY_TYPE_META } from '../../utils/deliveryTypeMeta';
import { applyOnlyUpsellRule } from '../../utils/gateways';
import { currencyLabelOf, formatMoney, marketOf, minPlanValue } from '../../utils/market';

const DURATION_OPTIONS = [
    { value: 'semanal', label: 'Semanal' },
    { value: 'quinzenal', label: 'Quinzenal' },
    { value: 'mensal', label: 'Mensal' },
    { value: 'trimestral', label: 'Trimestral' },
    { value: 'semestral', label: 'Semestral' },
    { value: 'anual', label: 'Anual' },
    { value: 'vitalicio', label: 'Vitalício' },
];

const COLOR_OPTIONS = [
    { label: 'Padrão', value: 'default', class: 'bg-gray-600' },
    { label: 'Vermelho', value: 'red', class: 'bg-red-600' },
    { label: 'Verde', value: 'green', class: 'bg-green-600' },
    { label: 'Azul', value: 'blue', class: 'bg-blue-600' },
];

// Tipos de entrega por plano expostos hoje. `default` = usa a Entrega Padrão
// configurada para o bot (que por sua vez cai no grupo VIP se também estiver
// em padrão). Os reservados (chamada de vídeo, bot/taxa) ainda não aparecem na
// UI — o backend cai no padrão até serem implementados.
// O label do `default` varia por contexto: PlanManager é reutilizado por
// Plans.jsx (padrão do bot), DisparoForm (idem) e UpsellForm (cai na Entrega
// Global dos Upsells) — por isso é parametrizável via `defaultDeliveryLabel`.
function buildDeliveryTypeOptions(defaultDeliveryLabel) {
    return [
        { value: 'default', label: defaultDeliveryLabel, ...DELIVERY_TYPE_META.default },
        { value: 'vip_group', label: 'Grupo VIP', ...DELIVERY_TYPE_META.vip_group },
        { value: 'external_link', label: 'Link externo', ...DELIVERY_TYPE_META.external_link },
        { value: 'message_only', label: 'Apenas mensagem', ...DELIVERY_TYPE_META.message_only },
        { value: 'only_upsell', label: 'Apenas Upsell', ...DELIVERY_TYPE_META.only_upsell },
    ];
}

function durationLabel(value) {
    return DURATION_OPTIONS.find((o) => o.value === value)?.label || 'Mensal';
}

function deliveryLabel(delivery) {
    const type = delivery?.type;
    if (type === 'vip_group') return 'Grupo VIP';
    if (type === 'external_link') return 'Link externo';
    if (type === 'message_only') return 'Apenas mensagem';
    if (type === 'only_upsell') return 'Apenas Upsell';
    return 'Entrega padrão';
}

function deliveryMeta(delivery) {
    const type = delivery?.type;
    return DELIVERY_TYPE_META[type] || DELIVERY_TYPE_META.default;
}


export default function PlanManager({
    initialPlans = null,
    onUpdate = () => { },
    label = "Planos de Pagamento",
    currencyMode = 'brl',
    // Modo LATAM: moeda em que os valores são escritos (MXN, USD, COP ou CLP).
    baseCurrency = null,
    enableDelivery = false,
    defaultDeliveryLabel = 'Usar Entrega Padrão',
    // "Apenas Upsell" indisponível (bot Global/com cartão) — vem da tela.
    onlyUpsellBlocked = false,
}) {
    const market = marketOf(currencyMode, baseCurrency);
    const currencySymbol = market.code;
    const currencyLabel = currencyLabelOf(currencyMode, baseCurrency);
    const deliveryTypeOptions = applyOnlyUpsellRule(buildDeliveryTypeOptions(defaultDeliveryLabel), onlyUpsellBlocked);

    // Estado interno - totalmente gerenciado aqui
    const [plans, setPlans] = useState({
        names: [],
        values: [],
        durations: [],
        color: [],
        deliveries: []   // alinhado por índice; null/ausente = entrega padrão
    });

    // Linhas com o painel de edição aberto (índice → boolean).
    const [openIndexes, setOpenIndexes] = useState(() => new Set());

    // Inicializar com dados fornecidos (se houver)
    useEffect(() => {
        if (initialPlans) {
            setPlans({
                names: Array.isArray(initialPlans.names) ? initialPlans.names : [],
                values: Array.isArray(initialPlans.values) ? initialPlans.values : [],
                durations: Array.isArray(initialPlans.durations) ? initialPlans.durations : [],
                color: Array.isArray(initialPlans.color) ? initialPlans.color : [],
                deliveries: Array.isArray(initialPlans.deliveries) ? initialPlans.deliveries : []
            });
        }
    }, [initialPlans]);

    const toggleOpen = (index) => {
        setOpenIndexes((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    // Notificar pai quando planos mudam
    const notifyUpdate = (updatedPlans) => {
        setPlans(updatedPlans);
        onUpdate(updatedPlans);
    };

    // Adicionar novo plano
    const handleAddPlan = () => {
        const newIndex = plans.names.length;
        notifyUpdate({
            names: [...plans.names, ""],
            values: [...plans.values, ""],
            durations: [...plans.durations, "mensal"],
            color: [...plans.color, "default"],
            deliveries: [...(plans.deliveries || []), null]
        });
        setOpenIndexes((prev) => new Set(prev).add(newIndex));
    };

    // Editar propriedade do plano
    const handleEditPlan = (index, field, value) => {
        const updatedPlans = { ...plans };
        if (field === 'name') {
            updatedPlans.names = [...plans.names];
            updatedPlans.names[index] = value;
        } else if (field === 'value') {
            updatedPlans.values = [...plans.values];
            updatedPlans.values[index] = value;
        } else if (field === 'duration') {
            updatedPlans.durations = [...plans.durations];
            updatedPlans.durations[index] = value;
        } else if (field === 'color') {
            updatedPlans.color = [...plans.color];
            updatedPlans.color[index] = value;
        }
        notifyUpdate(updatedPlans);
    };

    // Editar a entrega de um plano. `patch` mescla no objeto de entrega; quando o
    // tipo resultante é 'default' (ou vazio) grava null (= entrega padrão do fluxo).
    // `newValue` chega pronto do DeliveryTypeField (objeto completo ou null p/
    // "usar padrão") — só grava na posição certa, sem merge com o valor antigo.
    const handleEditDelivery = (index, newValue) => {
        const deliveries = [...(plans.deliveries || [])];
        while (deliveries.length <= index) deliveries.push(null);
        deliveries[index] = (newValue && newValue.type && newValue.type !== 'default') ? newValue : null;
        notifyUpdate({ ...plans, deliveries });
    };

    // Remover plano
    const handleRemovePlan = (index) => {
        notifyUpdate({
            names: plans.names.filter((_, i) => i !== index),
            values: plans.values.filter((_, i) => i !== index),
            durations: plans.durations.filter((_, i) => i !== index),
            color: plans.color.filter((_, i) => i !== index),
            deliveries: (plans.deliveries || []).filter((_, i) => i !== index)
        });
        setOpenIndexes((prev) => {
            const next = new Set();
            prev.forEach((i) => {
                if (i < index) next.add(i);
                else if (i > index) next.add(i - 1);
            });
            return next;
        });
    };

    return (
        <div className={label ? "mt-12" : ""}>
            {label ? <label className="spt-label">{label}</label> : null}

            <div className="mt-4 space-y-3">
                {plans.names.length > 0 ? (
                    plans.names.map((name, index) => {
                        const open = openIndexes.has(index);
                        const delivery = plans.deliveries?.[index] || null;
                        return (
                            <div
                                key={index}
                                className="panel-row rounded-lg"
                            >
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => toggleOpen(index)}
                                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleOpen(index)}
                                    className="flex items-center justify-between gap-3 p-4 cursor-pointer hover:bg-white/[0.02] transition-colors rounded-lg"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 bg-white/[0.03] border-white/[0.06]">
                                            <Package size={18} className="text-white/50" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-medium text-white truncate">
                                                {name || <span className="text-white/40 italic">Novo plano</span>}
                                            </p>
                                            <div className="flex items-center gap-2 text-sm text-white/30 flex-wrap">
                                                <span className="text-green-400 font-medium">{formatMoney(currencyMode, plans.values[index], baseCurrency)}</span>
                                                <span>•</span>
                                                <span>{durationLabel(plans.durations[index])}</span>
                                                <span
                                                    className={`inline-block w-2.5 h-2.5 rounded-full ${COLOR_OPTIONS.find((c) => c.value === plans.color[index])?.class || 'bg-gray-600'}`}
                                                    title="Cor do plano"
                                                />
                                                {enableDelivery && (() => {
                                                    const meta = deliveryMeta(delivery);
                                                    const DeliveryIcon = meta.icon;
                                                    return (
                                                        <>
                                                            <span>•</span>
                                                            <span className={`inline-flex items-center gap-1 text-xs ${meta.colorClass}`}>
                                                                <DeliveryIcon size={12} className="shrink-0" />
                                                                {deliveryLabel(delivery)}
                                                            </span>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            type="button"
                                            title="Remover plano"
                                            onClick={(e) => { e.stopPropagation(); handleRemovePlan(index); }}
                                            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                        <ChevronDown
                                            size={18}
                                            className={`text-white/30 transition-transform ${open ? 'rotate-180' : ''}`}
                                        />
                                    </div>
                                </div>

                                {open && (
                                    <div className="p-4 pt-0 border-t border-white/[0.06] space-y-3">
                                        <div className="flex flex-wrap gap-3 mt-3">
                                            <div className="flex-1 min-w-[160px]">
                                                <label className="text-xs text-white/40 mb-1 block">Nome do Plano</label>
                                                <input
                                                    placeholder="Ex: VIP SEMANAL"
                                                    value={name || ""}
                                                    onChange={(e) => handleEditPlan(index, 'name', e.target.value)}
                                                    className='input3 w-full'
                                                    required
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[120px]">
                                                <label className="text-xs text-white/40 mb-1 block">
                                                    Valor {currencyLabel} <span className="text-white/30">{currencySymbol}</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    step={market.decimals ? "0.01" : "1"}
                                                    min={String(minPlanValue(currencyMode, baseCurrency))}
                                                    placeholder="Ex: 10.00"
                                                    value={plans.values[index] ?? ''}
                                                    onChange={(e) => handleEditPlan(index, 'value', e.target.value)}
                                                    className='input3 w-full'
                                                    required
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[140px]">
                                                <label className="text-xs text-white/40 mb-1 block">Duração</label>
                                                <select
                                                    value={plans.durations[index] || 'mensal'}
                                                    onChange={(e) => handleEditPlan(index, 'duration', e.target.value)}
                                                    required
                                                    className="form-select w-full"
                                                >
                                                    {DURATION_OPTIONS.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="min-w-[110px]">
                                                <label className="text-xs text-white/40 mb-1 block">Cor</label>
                                                <div className="flex items-center gap-2 h-10">
                                                    {COLOR_OPTIONS.map((colorOption) => (
                                                        <button
                                                            key={colorOption.value}
                                                            type="button"
                                                            title={colorOption.label}
                                                            onClick={() => handleEditPlan(index, "color", colorOption.value)}
                                                            className={`w-5 h-5 rounded-full border-2 transition-all ${colorOption.class} ${plans.color[index] === colorOption.value
                                                                ? "border-white scale-110"
                                                                : "border-white/20"}`}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {enableDelivery && (
                                            <div>
                                                <label className="text-xs text-white/40 mb-1 block">Entrega deste plano</label>
                                                <DeliveryTypeField
                                                    value={delivery}
                                                    onChange={(newValue) => handleEditDelivery(index, newValue)}
                                                    options={deliveryTypeOptions}
                                                    messagePlaceholder="Mensagem entregue ao pagar"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center py-8 text-white/30 text-sm rounded-lg border border-dashed border-white/[0.08]">
                        Nenhum plano configurado.
                    </div>
                )}

                <button
                    type="button"
                    onClick={handleAddPlan}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-white/[0.08] text-white/40 hover:text-[var(--purple)] hover:border-[var(--purple-dark)] hover:bg-[var(--bg-primary)] transition-colors text-sm"
                >
                    <Plus size={16} /> Adicionar Plano
                </button>
            </div>
        </div>
    );
}
